// src/pages/QuotationList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaPrint,
  FaFilter,
  FaTimesCircle,
} from "react-icons/fa";
import { MdOutlineDoNotDisturbAlt } from "react-icons/md";
import { FiRotateCcw } from "react-icons/fi";
import Select from "react-select";
import toast from "react-hot-toast";

import { formatDate } from "../config/helperFunctions";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCustomersQuery } from "../toolkitServices/customerApi";
import {
  useGetQuotationsQuery,
  useDeleteQuotationMutation,
  useExpireQuotationMutation,
  useDownloadQuotationPdfMutation,
} from "../toolkitServices/quotationApi";
import InvoicePrintPreviewModal from "../components/InvoicePrintPreviewModal";

import ConfirmDialog from "../components/ConfirmDialog";
import { useSelector } from "react-redux";

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const canDelete = (user) => {
  const perm = user?.permissions?.find((p) => p.moduleKey === "quotation");
  return user?.role === "owner" || perm?.permissions?.delete;
};

const QuotationList = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  // --------- Filters & paging ----------
  const [search, setSearch] = useState("");
  const [branchId, setBranchId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // ✅ status filter
  const [status, setStatus] = useState(""); // "", pending, Draft, Converted, Expired

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // react-select ke selected values
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // row-level loader
  const [deletingId, setDeletingId] = useState(null);
  const [expiringId, setExpiringId] = useState(null);

  // ✅ Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMeta, setConfirmMeta] = useState({
    id: null,
    title: "",
    description: "",
    action: "delete", // "delete" | "expire"
  });

  // ✅ filter modal
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // ✅ status dropdown options
  const statusOptions = useMemo(
    () => [
      { value: "", label: "All" },
      { value: "pending", label: "Pending (Draft / Final)" },
      { value: "Converted", label: "Converted" },
      { value: "Expired", label: "Expired" },
    ],
    [],
  );

  // debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  /* --------- Fetch branches & customers for dropdowns ---------- */
  const {
    data: branchRes,
    isLoading: isBranchLoading,
    error: branchError,
  } = useGetAllBranchesQuery(
    { page: 1, limit: 1000, active: true },
    { refetchOnMountOrArgChange: false },
  );

  const {
    data: customerRes,
    isLoading: isCustomerLoading,
    error: customerError,
  } = useGetAllCustomersQuery(
    {
      page: 1,
      limit: 1000,
      active: true,
      sortBy: "customerName",
      sortOrder: "asc",
    },
    { refetchOnMountOrArgChange: false },
  );

  const branchOptions = useMemo(() => {
    const list = branchRes?.data ?? branchRes?.rows ?? branchRes?.items ?? [];
    if (!Array.isArray(list)) return [];
    return list
      .filter((b) => b && (b._id || b.id))
      .map((b) => ({
        value: b._id || b.id,
        label: b.branchName || b.name || b.code || "Unnamed branch",
      }));
  }, [branchRes]);

  const customerOptions = useMemo(() => {
    const list =
      customerRes?.data ?? customerRes?.rows ?? customerRes?.items ?? [];
    if (!Array.isArray(list)) return [];
    return list
      .filter((c) => c && (c._id || c.id))
      .map((c) => {
        const name = c.customerName || c.name || "Unnamed";
        const code = c.customerId || c.code || "";
        const phone =
          c.mobile ||
          c.phone ||
          c.contact ||
          (Array.isArray(c.contacts) ? c.contacts[0] : "");
        const parts = [name, code && `(${code})`, phone].filter(Boolean);
        return {
          value: c._id || c.id,
          label: parts.join(" "),
        };
      });
  }, [customerRes]);

  // --------- Fetch quotations ----------
  const queryArgs = {
    page,
    limit,
    branchId,
    customerId,
    from,
    to,
    status, // ✅ status filter param
    search: debouncedSearch,
    sortBy: "qtnNo",
    sortOrder: "desc",
  };

  const { data, isLoading, isFetching, error } = useGetQuotationsQuery(
    queryArgs,
    {
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  const [deleteQuotation, delState] = useDeleteQuotationMutation();
  const [expireQuotation, expState] = useExpireQuotationMutation();
  const [downloadQuotationPdf] = useDownloadQuotationPdfMutation();

  const [quotationPreview, setQuotationPreview] = useState({
    open: false,
    html: "",
    loading: false,
  });

  const rows = data?.rows || data?.data || [];
  const total = data?.total ?? data?.pagination?.totalDocs ?? rows.length;
  const totalPages =
    data?.pages ??
    data?.pagination?.totalPages ??
    Math.max(1, Math.ceil((total || 0) / (data?.limit || limit || 10)));

  // client-side sort (extra safety)
  const sortedRows = useMemo(() => {
    const list = Array.isArray(rows) ? [...rows] : [];
    list.sort((a, b) => {
      const aNo = Number(a?.quotationNo ?? a?.qtnNo ?? 0);
      const bNo = Number(b?.quotationNo ?? b?.qtnNo ?? 0);
      return bNo - aNo;
    });
    return list;
  }, [rows]);

  // (optional) local filter search - backend already handles `q`
  const filteredRows = useMemo(() => {
    if (!debouncedSearch) return sortedRows;
    const q = debouncedSearch.toLowerCase();
    return sortedRows.filter((qt) => {
      const hay = [
        qt?.quotationNo,
        qt?.quotationCode,
        qt?.notes,
        qt?.customer?.customerName,
        qt?.customer?.customerId,
        qt?.branch?.branchName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sortedRows, debouncedSearch]);

  // Keyboard: Ctrl+P -> create quotation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/quotations/quotation-list/create-quotation");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // ✅ whenever filters change -> page 1
  useEffect(() => {
    setPage(1);
  }, [branchId, customerId, from, to, status, debouncedSearch, limit]);

  // ✅ Open confirm dialog for delete
  const askDelete = (qt) => {
    const id = qt?._id || qt?.id;
    if (!id) return;

    const qNo =
      qt?.quotationNo ??
      qt?.quotationCode ??
      qt?.quotationId ??
      qt?.qtnNo ??
      "—";

    const cust =
      qt?.customerCode ||
      qt?.customerName ||
      qt?.customer?.customerName ||
      qt?.customer?.name ||
      "—";

    setConfirmMeta({
      id,
      title: `Delete quotation ${qNo}?`,
      description: `Customer: ${cust}`,
      action: "delete",
    });
    setConfirmOpen(true);
  };

  // ✅ Open confirm dialog for expire
  const askExpire = (qt) => {
    const id = qt?._id || qt?.id;
    if (!id) return;

    const qNo =
      qt?.quotationNo ??
      qt?.quotationCode ??
      qt?.quotationId ??
      qt?.qtnNo ??
      "—";

    const cust =
      qt?.customerCode ||
      qt?.customerName ||
      qt?.customer?.customerName ||
      qt?.customer?.name ||
      "—";

    setConfirmMeta({
      id,
      title: `Expire quotation ${qNo}?`,
      description: `Customer: ${cust}`,
      action: "expire",
    });
    setConfirmOpen(true);
  };

  // ✅ Confirm dialog handler (delete/expire)
  const handleConfirm = async () => {
    const id = confirmMeta?.id;
    if (!id) return;

    if (confirmMeta.action === "delete") {
      try {
        setDeletingId(id);
        await deleteQuotation(id).unwrap();
        toast.success("Quotation deleted");
        setConfirmOpen(false);
        setConfirmMeta({
          id: null,
          title: "",
          description: "",
          action: "delete",
        });
      } catch (e) {
        console.error(e);
        toast.error(e?.data?.message || e?.error || "Failed to delete");
      } finally {
        setDeletingId(null);
      }
      return;
    }

    if (confirmMeta.action === "expire") {
      try {
        setExpiringId(id);
        await expireQuotation(id).unwrap();
        toast.success("Quotation expired");
        setConfirmOpen(false);
        setConfirmMeta({
          id: null,
          title: "",
          description: "",
          action: "delete",
        });
      } catch (e) {
        console.error(e);
        toast.error(e?.data?.message || e?.error || "Failed to expire");
      } finally {
        setExpiringId(null);
      }
    }
  };

  const isConfirmBusy =
    (delState.isLoading && deletingId && deletingId === confirmMeta?.id) ||
    (expState.isLoading && expiringId && expiringId === confirmMeta?.id);

  // Print/View quotation PDF
  const handlePrint = async (id) => {
    if (!id) return;

    try {
      setQuotationPreview({
        open: true,
        html: "",
        loading: true,
      });

      const htmlContent = await downloadQuotationPdf(id).unwrap();

      setQuotationPreview({
        open: true,
        html: htmlContent,
        loading: false,
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load quotation preview.");

      setQuotationPreview({
        open: false,
        html: "",
        loading: false,
      });
    }
  };

  const closeQuotationPreview = () => {
    setQuotationPreview({
      open: false,
      html: "",
      loading: false,
    });
  };

  // react-select styling
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 34,
      height: 34,
      borderRadius: 8,
      borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 1px #7c3aed33" : "none",
      "&:hover": { borderColor: "#7c3aed" },
      fontSize: "0.8rem",
      backgroundColor: "white",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    singleValue: (base) => ({
      ...base,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: "#111827",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: "0.8rem",
    }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
    indicatorsContainer: (base) => ({ ...base, height: 34 }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.8rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      backgroundColor: state.isFocused
        ? "#ede9fe"
        : state.isSelected
          ? "#ddd6fe"
          : "white",
      color: "#111827",
    }),
    menu: (base) => ({ ...base, zIndex: 40 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const paginationSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 32,
      height: 32,
      borderRadius: 6,
      borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
      fontSize: 10,
    }),
    valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
    menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
    option: (base, state) => ({
      ...base,
      padding: "6px 8px",
      backgroundColor: state.isSelected
        ? "#9481ce"
        : state.isFocused
          ? "#ede9fe"
          : "white",
      color: state.isSelected ? "white" : "#334155",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  // ✅ chips
  const chips = useMemo(() => {
    const arr = [];
    if (search?.trim())
      arr.push({ key: "search", label: `Search: ${search.trim()}` });
    if (selectedBranch?.label)
      arr.push({ key: "branch", label: `Branch: ${selectedBranch.label}` });
    if (selectedCustomer?.label)
      arr.push({
        key: "customer",
        label: `Customer: ${selectedCustomer.label}`,
      });
    if (from) arr.push({ key: "from", label: `From: ${from}` });
    if (to) arr.push({ key: "to", label: `To: ${to}` });
    if (status) {
      arr.push({
        key: "status",
        label: status === "pending" ? "Status: Pending" : `Status: ${status}`,
      });
    }
    return arr;
  }, [search, selectedBranch, selectedCustomer, from, to, status]);

  const clearChip = (k) => {
    if (k === "search") setSearch("");
    if (k === "branch") {
      setSelectedBranch(null);
      setBranchId("");
    }
    if (k === "customer") {
      setSelectedCustomer(null);
      setCustomerId("");
    }
    if (k === "from") setFrom("");
    if (k === "to") setTo("");
    if (k === "status") setStatus("");
  };

  const resetFilters = () => {
    setSearch("");
    setSelectedBranch(null);
    setBranchId("");
    setSelectedCustomer(null);
    setCustomerId("");
    setFrom("");
    setTo("");
    setStatus(""); // ✅ reset status
  };

  // NOTE: In your original tbody you are using `sortedRows`.
  // If you want local search filtering too, replace `sortedRows` with `filteredRows`.
  const tableRows = filteredRows;

  return (
    <div className="relative bg-white shadow-lg rounded-2xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {/* ✅ Confirm dialog */}
      <ConfirmDialog
        open={confirmOpen}
        title={confirmMeta?.title || "Confirm"}
        message={
          confirmMeta?.description || "Are you sure you want to proceed?"
        }
        confirmText={
          isConfirmBusy
            ? confirmMeta?.action === "expire"
              ? "Expiring..."
              : "Deleting..."
            : confirmMeta?.action === "expire"
              ? "Expire"
              : "Delete"
        }
        cancelText="Cancel"
        onCancel={() => {
          if (isConfirmBusy) return;
          setConfirmOpen(false);
        }}
        onConfirm={handleConfirm}
        disabled={isConfirmBusy}
      />

      {/* Loader */}
      {isLoading && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-slate-600 text-xs sm:text-sm">
            <Spinner size={22} />
            <span>Loading quotations…</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 rounded-t-xl bg-[#9481ce] text-white">
        <div className="flex items-center gap-2">
          <h2 className="text-base sm:text-lg font-semibold tracking-tight">
            Quotation List
          </h2>
          {isFetching && rows.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-[11px] text-violet-100/90">
            Ctrl + P to create new
          </span>
          <Link
            to="/quotations/quotation-list/create-quotation"
            className="inline-flex items-center gap-1 rounded-full bg-white text-[#6b4fd8] px-3 sm:px-4 py-1.5 text-[11px] sm:text-xs font-semibold shadow-sm hover:bg-violet-50 transition"
          >
            + Add Quotation
          </Link>
        </div>
      </div>

      {/* Filters bar - URD style */}
      <div className="px-2 py-2 sm:px-4 border-b bg-slate-50/80">
        <div className="flex items-end gap-2 sm:gap-3 flex-nowrap w-full min-w-0">
          {/* Search */}
          <div className="flex min-w-0 flex-1 flex-col gap-[1px]">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block">
              Search
            </label>
            <input
              type="text"
              placeholder="Search quotation / customer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 min-w-0 w-full rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400 sm:px-3"
            />
          </div>

          {/* Filters button */}
          <div className="flex w-auto shrink-0 flex-col gap-[1px]">
            <label className="hidden text-[11px] font-medium text-slate-600 sm:block sm:invisible">
              Filters
            </label>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex h-8 w-8 items-center justify-center gap-1 rounded-full border border-violet-200 bg-white text-[#6b59c6] hover:bg-violet-50 sm:w-auto sm:px-3"
              title="Advanced filters"
              aria-label="Advanced filters"
            >
              <FaFilter />
              <span className="hidden sm:inline text-[11px]">Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Errors */}
      {error && (
        <div className="px-3 sm:px-4 py-1.5 text-red-600 text-xs border-b bg-red-50">
          {error?.data?.message || "Failed to load quotations"}
        </div>
      )}
      {branchError && (
        <div className="px-3 sm:px-4 py-1.5 text-[11px] text-yellow-800 bg-yellow-50 border-b">
          Failed to load branches (filter will still work without it)
        </div>
      )}
      {customerError && (
        <div className="px-3 sm:px-4 py-1.5 text-[11px] text-yellow-800 bg-yellow-50 border-b">
          Failed to load customers (filter will still work without it)
        </div>
      )}

      {/* Chips */}
      {chips.length > 0 && (
        <div className="px-3 sm:px-4 py-1.5 border-b bg-slate-50 flex items-center gap-2">
          <div className="flex items-center gap-2 flex-nowrap overflow-x-auto min-w-0 pr-2">
            {chips.map((c) => (
              <span
                key={c.key}
                className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm whitespace-nowrap flex-shrink-0"
              >
                {c.label}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => clearChip(c.key)}
                  title="Clear"
                >
                  <FaTimesCircle />
                </button>
              </span>
            ))}
          </div>

          <button
            className="ml-auto text-[11px] text-[#6b59c6] hover:underline flex-shrink-0"
            onClick={resetFilters}
            title="Reset all"
          >
            Reset
          </button>
        </div>
      )}

      {/* Table */}
      <div className="relative flex-1 overflow-x-auto overflow-y-auto">
        <table className="min-w-full text-[10px] sm:text-xs text-left whitespace-nowrap">
          <thead className="sticky top-0 z-40 bg-[#e6e1f5] text-[8px] uppercase text-[#4b3d91] sm:text-[9px]">
            <tr>
              <th className="px-1 sm:px-2 py-1.5">Quote No</th>
              <th className="px-1 sm:px-2 py-1.5">Quote Code</th>
              <th className="px-1 sm:px-2 py-1.5">Customer</th>
              <th className="px-1 sm:px-2 py-1.5">Branch</th>
              <th className="px-1 sm:px-2 py-1.5">Date</th>
              <th className="px-1 sm:px-2 py-1.5 text-right">Total Amount</th>
              <th className="px-1 sm:px-2 py-1.5 text-right">Status</th>
              <th className="sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] px-1 py-1.5 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px] sm:px-2">
                <span className="sm:hidden">Act</span>
                <span className="hidden sm:inline">Action</span>
              </th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence>
              {tableRows?.length
                ? tableRows.map((qt, index) => {
                    const id = qt?._id || qt?.id;

                    const qNo =
                      qt?.quotationNo ??
                      qt?.quotationCode ??
                      qt?.quotationId ??
                      qt?.qtnNo ??
                      "—";

                    const cust =
                      qt?.customerCode ||
                      qt?.customerName ||
                      qt?.customer?.customerName ||
                      qt?.customer?.name ||
                      "—";

                    const branch =
                      qt?.branch?.branchName ||
                      qt?.branchName ||
                      qt?.branch?.name ||
                      "—";

                    const dt =
                      qt?.quotationDate || qt?.date || qt?.createdAt || null;

                    const totalAmt =
                      qt?.totals?.grandTotal ??
                      qt?.grandTotal ??
                      qt?.computedGrandTotal ??
                      0;

                    const statusLc = String(qt?.status || "")
                      .trim()
                      .toLowerCase();
                    const isFinal = statusLc === "final";
                    const isExpired = statusLc === "expired";

                    const showExpireIcon = isFinal || isExpired;

                    const isDeleting = delState.isLoading && deletingId === id;
                    const isExpiring = expState.isLoading && expiringId === id;
                    const rowBusy = isDeleting || isExpiring;

                    const expireDisabled = !isFinal || rowBusy;
                    const expireTitle = rowBusy
                      ? "Please wait…"
                      : isExpired
                        ? "Already expired"
                        : "Expire quotation";

                    return (
                      <motion.tr
                        key={id || index}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18, delay: index * 0.01 }}
                        className={`group border-t transition hover:bg-[#f4f0ff] ${
                          rowBusy ? "opacity-60" : ""
                        }`}
                      >
                        <td className="px-1 sm:px-2 py-2 font-medium">
                          {id ? (
                            <button
                              type="button"
                              className="text-[#6b59c6] underline underline-offset-2 hover:opacity-80"
                              title="View / Print quotation"
                              onClick={() => handlePrint(id)}
                            >
                              {qNo}
                            </button>
                          ) : (
                            qNo
                          )}
                        </td>

                        <td className="px-1 sm:px-2 py-1.5 max-w-[180px]">
                          <span className="block truncate" title={qt?.qtnCode}>
                            {qt?.qtnCode || "—"}
                          </span>
                        </td>

                        <td className="px-1 sm:px-2 py-1.5 max-w-[180px]">
                          <span className="block truncate" title={cust}>
                            {cust}
                          </span>
                        </td>

                        <td className="px-1 sm:px-2 py-1.5 max-w-[160px]">
                          <span className="block truncate" title={branch}>
                            {branch}
                          </span>
                        </td>

                        <td className="px-1 sm:px-2 py-1.5">
                          {dt ? formatDate(dt) : "—"}
                        </td>

                        <td className="px-1 sm:px-2 py-1.5 text-right">
                          ₹ {Number(totalAmt || 0).toFixed(2)}
                        </td>

                        <td className="px-1 sm:px-2 py-1.5 text-right">
                          {qt?.status === "Final"
                            ? "Not Converted"
                            : qt?.status}
                        </td>

                        <td className="sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 px-1 py-1.5 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px] sm:px-2">
                          {/* Print */}
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                            title="Print / View PDF"
                            onClick={() => handlePrint(id)}
                          >
                            <FaPrint />
                          </motion.button>

                          {/* Edit */}
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-[#6b59c6] hover:bg-violet-50 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                            title="Edit"
                            onClick={() =>
                              navigate(`/quotations/quotation-list/${id}/edit`)
                            }
                          >
                            <FaPencilAlt />
                          </motion.button>

                          {/* Expire */}
                          {showExpireIcon && (
                            <motion.button
                              whileHover={expireDisabled ? {} : { scale: 1.05 }}
                              whileTap={expireDisabled ? {} : { scale: 0.95 }}
                              className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px] ${
                                expireDisabled
                                  ? "text-slate-400 bg-transparent cursor-not-allowed"
                                  : "text-[#e11d48] hover:bg-[#e11d48]/10"
                              }`}
                              title={expireTitle}
                              disabled={expireDisabled}
                              onClick={() => {
                                if (expireDisabled) return;
                                askExpire(qt);
                              }}
                            >
                              {isExpiring ? (
                                <Spinner size={12} />
                              ) : (
                                <MdOutlineDoNotDisturbAlt />
                              )}
                            </motion.button>
                          )}

                          {/* Delete */}
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] text-rose-500 hover:bg-rose-50 disabled:opacity-40 sm:h-7 sm:w-7 sm:rounded-lg sm:text-[11px]"
                            title="Delete"
                            disabled={rowBusy}
                            onClick={() => {
                              if (canDelete(user)) {
                                askDelete(qt);
                              } else {
                                toast.error(
                                  "You don't have permission to delete",
                                );
                              }
                            }}
                          >
                            {isDeleting ? (
                              <Spinner size={12} />
                            ) : (
                              <FaTrashAlt />
                            )}
                          </motion.button>
                        </td>
                      </motion.tr>
                    );
                  })
                : !isLoading && (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td
                        colSpan="8"
                        className="text-center text-gray-500 py-6"
                      >
                        No matching records found.
                      </td>
                    </motion.tr>
                  )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="shrink-0 flex items-center justify-between px-2 py-1.5 sm:px-4 sm:py-2 border-t text-[10px] sm:text-sm bg-slate-50/60 rounded-b-2xl gap-2">
        <div className="hidden text-slate-600 sm:block">
          Page <span className="font-semibold">{page}</span> of{" "}
          <span className="font-semibold">{totalPages}</span> • {total ?? 0}{" "}
          record(s)
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex shrink-0 items-center gap-1 text-slate-600">
            <span className="hidden text-xs sm:inline">Rows / page</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => setLimit(Number(option?.value) || 10)}
              isDisabled={isFetching || isLoading}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none sm:justify-start sm:gap-2">
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page <= 1 || isFetching || isLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &lsaquo;
              </span>
              <span className="hidden sm:inline">Prev</span>
            </button>

            <span className="flex items-center gap-1 text-slate-600 sm:hidden">
              <Select
                value={{
                  value: totalPages ? page : 1,
                  label: String(totalPages ? page : 1),
                }}
                options={Array.from(
                  { length: Math.max(1, totalPages) },
                  (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }),
                )}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[52px]"
                aria-label="Select page number"
              />
              <span>/ {Math.max(1, totalPages)}</span>
            </span>

            <span className="hidden items-center gap-1 text-slate-600 sm:flex">
              <span>Page</span>
              <Select
                value={{
                  value: totalPages ? page : 1,
                  label: String(totalPages ? page : 1),
                }}
                options={Array.from(
                  { length: Math.max(1, totalPages) },
                  (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }),
                )}
                onChange={(option) => setPage(Number(option?.value) || 1)}
                isDisabled={isFetching || isLoading}
                isSearchable={false}
                menuPlacement="top"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                menuShouldScrollIntoView={false}
                styles={paginationSelectStyles}
                className="w-[58px]"
                aria-label="Select page number"
              />
              <span>of {Math.max(1, totalPages)}</span>
            </span>

            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white text-base hover:bg-white disabled:opacity-50 sm:h-auto sm:w-auto sm:px-3 sm:py-1 sm:text-sm"
              disabled={page >= totalPages || isFetching || isLoading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &rsaquo;
              </span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Filter modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Advanced filters
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Filter quotations by branch, customer, status and date
                    range.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 space-y-4 text-[11px] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 mb-1">Branch</label>
                    <Select
                      isClearable
                      isSearchable
                      isDisabled={isBranchLoading}
                      options={branchOptions}
                      value={selectedBranch}
                      onChange={(opt) => {
                        setSelectedBranch(opt);
                        setBranchId(opt?.value || "");
                      }}
                      placeholder="All branches"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 mb-1">
                      Customer
                    </label>
                    <Select
                      isClearable
                      isSearchable
                      isDisabled={isCustomerLoading}
                      options={customerOptions}
                      value={selectedCustomer}
                      onChange={(opt) => {
                        setSelectedCustomer(opt);
                        setCustomerId(opt?.value || "");
                      }}
                      placeholder="All customers"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  {/* ✅ Status in modal */}
                  <div>
                    <label className="block text-slate-600 mb-1">Status</label>
                    <Select
                      isSearchable={false}
                      options={statusOptions}
                      value={
                        statusOptions.find((o) => o.value === status) || null
                      }
                      onChange={(opt) => setStatus(opt?.value || "")}
                      placeholder="All"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 mb-1">From</label>
                    <input
                      type="date"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 mb-1">To</label>
                    <input
                      type="date"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-slate-600 mb-1">Search</label>
                    <input
                      type="text"
                      placeholder="Quotation no, customer, notes…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <InvoicePrintPreviewModal
        open={quotationPreview.open}
        html={quotationPreview.html}
        loading={quotationPreview.loading}
        onClose={closeQuotationPreview}
      />
    </div>
  );
};

export default QuotationList;
